/* Roteador do modo mock.

   Faz match do path contra os MESMOS padrões de rota do backend Flask
   (/professor/turma/:id, /escola/:id/kpis, …) em vez de devolver um objeto
   qualquer. Assim o mapeamento das 22 rotas é exercitado de verdade em
   desenvolvimento: se uma tela chamar um path que o backend não expõe, o erro
   aparece agora, e não na virada.

   As fixtures foram CAPTURADAS do próprio backend rodando em DEMO_MODE=1
   (mock_data.py). São 114 respostas com o contrato real e ids consistentes
   entre listas e detalhes, então o drill-down rede → região → escola → turma →
   aluno navega de fato. Nada aqui foi inventado à mão.

   Para recapturar (ex.: depois de mudar um endpoint):
     DEMO_MODE=1 python3 Analytics_engajamento/dashboard/backend/server.py
     python3 <scratchpad>/capturar_fixtures.py <destino>
*/

import fixtures from "./fixtures.json";

/* Latência simulada: sem ela os estados de carregamento nunca aparecem em
   desenvolvimento e só descobriríamos os problemas de skeleton em produção. */
const LATENCIA_MS = 150;

const PARAMS_IGNORADOS = ["inicio", "fim", "ano", "page", "per_page", "nome"];

const chaveDe = (path, params) => {
  const pares = Object.keys(params || {})
    .filter(
      k => params[k] !== undefined && params[k] !== null && params[k] !== ""
    )
    .map(k => [k, String(params[k])]);
  if (!pares.length) return path;
  const qs = pares
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  return `${path}?${qs}`;
};

/* Padrão da rota: troca os ids numéricos por ":id".
   `/professor/turma/41129/execucao` -> `/professor/turma/:id/execucao` */
const padraoDe = path => path.replace(/\/\d+/g, "/:id");

/* Índice padrão-de-rota -> chaves de fixture, montado uma vez. */
const PorPadrao = (() => {
  const mapa = new Map();
  Object.keys(fixtures).forEach(chave => {
    const p = padraoDe(chave.split("?")[0]);
    if (!mapa.has(p)) mapa.set(p, []);
    mapa.get(p).push(chave);
  });
  mapa.forEach(lista => lista.sort());
  return mapa;
})();

/* Hash estável de string -> inteiro. Serve para escolher SEMPRE a mesma
   variante para o mesmo id: a escola 3007 mostra sempre os mesmos números, em
   vez de mudar a cada render. */
const hash = texto => {
  let h = 0;
  for (let i = 0; i < texto.length; i += 1) {
    h = (h * 31 + texto.charCodeAt(i)) | 0; // eslint-disable-line no-bitwise
  }
  return Math.abs(h);
};

/* Resolução em três tentativas, da mais fiel para a mais tolerante:

   1. chave exata (path + querystring);
   2. mesmo path, ignorando filtros de período/paginação — o dado é coerente,
      só não reflete aquele filtro;
   3. MESMO PADRÃO DE ROTA, com outro id.

   O passo 3 é o que faz o drill-down funcionar até o fim. Capturar todas as
   combinações de rede → região → escola → turma → aluno geraria megabytes de
   fixture (só o engajamento por turma dava 1,3 MB); em vez disso capturamos
   uma amostra de cada nível e reusamos por padrão. A variante é escolhida por
   hash do id, então cada escola/turma mostra números diferentes, sempre os
   mesmos para o mesmo id.

   Com o backend real (REACT_APP_IE_MOCK=off) nada disso roda: cada id devolve
   os seus próprios dados. */
const acharAproximado = (path, params) => {
  const exata = fixtures[chaveDe(path, params)];
  if (exata !== undefined) return exata;

  const relevantes = Object.keys(params || {}).filter(
    k =>
      !PARAMS_IGNORADOS.includes(k) &&
      params[k] !== undefined &&
      params[k] !== null &&
      params[k] !== ""
  );
  const tentativa = {};
  relevantes.forEach(k => {
    tentativa[k] = params[k];
  });
  const semFiltrosDePeriodo = fixtures[chaveDe(path, tentativa)];
  if (semFiltrosDePeriodo !== undefined) return semFiltrosDePeriodo;

  // mesmo path literal, preferindo a fixture com menos parâmetros
  const mesmoPath = Object.keys(fixtures)
    .filter(k => k === path || k.startsWith(`${path}?`))
    .sort((a, b) => a.length - b.length);
  if (mesmoPath.length) return fixtures[mesmoPath[0]];

  // mesmo PADRÃO de rota, id diferente
  const candidatas = PorPadrao.get(padraoDe(path));
  if (candidatas && candidatas.length) {
    // prefere as que têm a mesma granularidade pedida, quando houver
    const gran = params?.granularidade;
    const comGran = gran
      ? candidatas.filter(k => k.includes(`granularidade=${gran}`))
      : [];
    const pool = comGran.length ? comGran : candidatas;
    return fixtures[pool[hash(path) % pool.length]];
  }

  return undefined;
};

export function resolverMock(metodo, path, params) {
  return new Promise((resolve, reject) => {
    setTimeout(() => {
      if (metodo === "POST" && path === "/sync") {
        resolve({
          ok: false,
          error: "Sincronização indisponível no modo mock."
        });
        return;
      }

      const dado = acharAproximado(path, params);
      if (dado === undefined) {
        // Falha ALTO E CLARO: rota não mapeada é descasamento de contrato, e
        // precisa aparecer em desenvolvimento.
        reject(
          new Error(
            `[IE mock] sem fixture para ${metodo} ${chaveDe(path, params)}. ` +
              `Recapture as fixtures ou confira o path em lib/api.js.`
          )
        );
        return;
      }
      resolve(dado);
    }, LATENCIA_MS);
  });
}

export const rotasDisponiveis = () => Object.keys(fixtures);
